import { useCallback, useRef } from 'react'

import { useAuthedFetch } from '../../auth/useAuthedFetch'
import withFetchCancel from '../../libs/withFetchCancel'
import { useMutation, useQuery } from 'react-query'
import { getGeneratedReportsFilters, getPageParams } from './business'

const BACKEND_ENDPOINT = process.env.REACT_APP_BACKEND_ENDPOINT

const REPORTS_KEY = 'reports'

const useGeneratedReportsTracking = ({ clientCode, filters, config = {} }) => {
  const queryParams = getGeneratedReportsFilters(filters)

  const authedFetch = useAuthedFetch()

  const encodeClientCode = encodeURIComponent(clientCode);

  const refetchRef = useRef()

  const query = useQuery(
    ['generatedReports'],
    () => {
      if (!config?.noExecute && clientCode) {
        const fetchPromise = withFetchCancel(signal =>
          authedFetch(
            'GET',
            `${BACKEND_ENDPOINT}/v1.0/reporting/clients/${encodeClientCode}/generated-reports?${queryParams}`,
            {
              parseJSON: true,
              signal,
            },
          ),
        )
        refetchRef.current = () => fetchPromise
        return fetchPromise
      } else {
        return []
      }
    },
    {
      select: ({ data }) => data,
      ...config,
    },
  )

  const manualRefetch = useCallback(() => {
    if (refetchRef.current) {
      refetchRef.current()
    }
  }, [])

  return { ...query, manualRefetch }
}

const useReportConfigTracking = ({ filters, config = {} }) => {
  const queryParams = getPageParams(filters)

  const authedFetch = useAuthedFetch()

  const refetchRef = useRef()

  const queryKey = [REPORTS_KEY]

  const query = useQuery(
    queryKey,
    () => {
      if (!config?.noExecute) {
        const fetchPromise = withFetchCancel(signal =>
          authedFetch(
            'GET',
            `${BACKEND_ENDPOINT}/v1.0/reporting/client-reports?${queryParams}`,
            {
              parseJSON: true,
              signal,
            },
          ),
        )
        refetchRef.current = () => fetchPromise
        return fetchPromise
      } else {
        return []
      }
    },
    {
      select: ({ data }) => data,
      ...config,
    },
  )

  const manualRefetch = useCallback(() => {
    if (refetchRef.current) {
      refetchRef.current()
    }
  }, [])

  return { ...query, manualRefetch }
}

const useReportSubscriptions = ({ clientCode, config = {} }) => {
  const authedFetch = useAuthedFetch()

  const encodeClientCode = encodeURIComponent(clientCode);

  const refetchRef = useRef()

  const query = useQuery(
    ['reportSubscriptions'],
    () => {
      if (clientCode) {
        const fetchPromise = withFetchCancel(signal =>
          authedFetch(
            'GET',
            `${BACKEND_ENDPOINT}/v1.0/reporting/reports-subscriptions?clientCode=${encodeClientCode}`,
            {
              parseJSON: true,
              signal,
            },
          ),
        )
        refetchRef.current = () => fetchPromise
        return fetchPromise
      } else {
        return []
      }
    },
    {
      select: ({ data }) => data,
      ...config,
    },
  )

  const manualRefetch = useCallback(() => {
    if (refetchRef.current) {
      refetchRef.current()
    }
  }, [])

  return { ...query, manualRefetch }
}

const useUpdateSubscriptionReport = (clientCode, onSuccess, onError) => {
  const authedFetch = useAuthedFetch()

  const encodeClientCode = encodeURIComponent(clientCode);

  return useMutation(
    ({ reportId, enabled, email, webUserCode }) =>
      withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/v1.0/reporting/clients/${encodeClientCode}/reports-subscriptions/${reportId}`,
          {
            signal,
            parseJSON: true,
            data: {
              subscriptionEnabled: enabled,
              notifications: [
                {
                  email: email ?? '',
                },
              ],
              audit: { user_code: clientCode },
              webUserCode: webUserCode,
            },
          },
        ),
      ),
    {
      useErrorBoundary: false,
      onSuccess: e => onSuccess(e?.data),
      onError: () => onError(),
    },
  )
}

const useDownloadReport = (clientCode, onSuccess, onError) => {
  const authedFetch = useAuthedFetch()

  const encodeClientCode = encodeURIComponent(clientCode);

  return useMutation(
    ({ resource_id, resource_provider }) =>
      withFetchCancel(signal =>
        authedFetch(
          'POST',
          `${BACKEND_ENDPOINT}/v1.0/reporting/clients/${encodeClientCode}/generated-reports/rpc-download-report-file`,
          {
            signal,
            parseJSON: true,
            data: {
              resource_provider: resource_provider,
              resource_id: resource_id,
            },
          },
        ),
      ),
    {
      useErrorBoundary: false,
    },
  )
}

export {
  useReportConfigTracking,
  useReportSubscriptions,
  useGeneratedReportsTracking,
  useDownloadReport,
  useUpdateSubscriptionReport,
}
